<template>
  <div class="sql-view-filter-builder">
    <!-- The Add filter action lives in the parent section header -->
    <div v-if="!filters.length" class="text-caption text--disabled py-2">
      No filters. The widget shows every row of the data source.
    </div>

    <div
      v-for="(filter, index) in filters"
      :key="index"
      class="sql-view-filter-row d-flex align-center mb-2"
      style="gap: 8px">
      <!-- Column -->
      <v-select
        v-model="filter.column"
        :items="columns"
        item-text="name"
        item-value="name"
        placeholder="Column"
        dense
        outlined
        hide-details
        style="flex: 2"
        @change="onColumnChange(filter)">
        <template #item="{ item }">
          <div class="d-flex align-center" style="gap: 6px">
            <v-icon x-small color="grey">{{ typeIcon(item.dataType) }}</v-icon>
            <span>{{ item.name }}</span>
            <span class="text-caption text--disabled ml-1">({{ friendlyTypeLabel(item.dataType) }})</span>
          </div>
        </template>
      </v-select>

      <!-- Operator -->
      <v-select
        v-model="filter.operator"
        :items="operatorsForColumn(filter.column)"
        item-text="label"
        item-value="value"
        placeholder="Operator"
        dense
        outlined
        hide-details
        style="flex: 1.5"
        @change="onOperatorChange(filter)">
      </v-select>

      <!-- Between: two inputs (from / to), stored as [from, to] -->
      <div
        v-if="isBetweenOperator(filter.operator)"
        class="d-flex align-center"
        style="flex: 2; gap: 4px">
        <template v-for="(end, endIndex) in betweenEnds(filter)">
          <v-menu
            v-if="isDatePickerType(getColumnType(filter.column))"
            :key="`date-${endIndex}`"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="end"
                :placeholder="betweenPlaceholder(endIndex)"
                :aria-label="`Filter ${index + 1} ${betweenPlaceholder(endIndex)}`"
                dense
                outlined
                hide-details
                readonly
                v-bind="attrs"
                v-on="on">
              </v-text-field>
            </template>
            <v-date-picker
              :value="end || undefined"
              no-title
              scrollable
              @input="setBetweenEnd(filter, endIndex, $event)">
            </v-date-picker>
          </v-menu>
          <v-text-field
            v-else
            :key="`text-${endIndex}`"
            :value="end"
            :placeholder="betweenPlaceholder(endIndex)"
            :aria-label="`Filter ${index + 1} ${betweenPlaceholder(endIndex)}`"
            :type="isNumericType(getColumnType(filter.column)) ? 'number' : 'text'"
            dense
            outlined
            hide-details
            @input="setBetweenEnd(filter, endIndex, $event)">
          </v-text-field>
        </template>
      </div>

      <!-- Value input (hidden for null operators) -->
      <template v-else-if="!isNullOperator(filter.operator)">
        <!-- Date picker for date columns -->
        <v-menu
          v-if="isDatePickerType(getColumnType(filter.column))"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="filter.value"
              placeholder="Select date"
              dense
              outlined
              hide-details
              readonly
              style="flex: 2"
              v-bind="attrs"
              v-on="on">
            </v-text-field>
          </template>
          <v-date-picker
            v-model="filter.value"
            no-title
            scrollable
            @input="emitChange">
          </v-date-picker>
        </v-menu>

        <!-- Number input for numeric columns -->
        <v-text-field
          v-else-if="isNumericType(getColumnType(filter.column))"
          v-model="filter.value"
          placeholder="Value"
          type="number"
          dense
          outlined
          hide-details
          style="flex: 2"
          @input="emitChange">
        </v-text-field>

        <!-- Text input for everything else -->
        <v-text-field
          v-else
          v-model="filter.value"
          placeholder="Value"
          dense
          outlined
          hide-details
          style="flex: 2"
          @input="emitChange">
        </v-text-field>
      </template>
      <div v-else style="flex: 2"></div>

      <!-- Remove button -->
      <v-btn icon x-small color="error" :aria-label="`Remove filter ${index + 1}`" @click="removeFilter(index)">
        <v-icon x-small>mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { NUMERIC_SQL_TYPES, DATE_SQL_TYPES, friendlyType } from '../utils/columnTypes';
import {
  BETWEEN_OPERATOR,
  parseBetweenValue,
  withBetweenPart,
  filterValueForOperator,
} from '../utils/betweenFilter';

// Columns edited with a date picker. Other date types keep a text input.
const DATE_PICKER_SQL_TYPE = 'datetime';
const BETWEEN_PLACEHOLDERS = Object.freeze(['From', 'To']);

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      filters: this.value ? [...this.value.map(f => ({ ...f }))] : [],
    };
  },

  watch: {
    value: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.filters)) {
          this.filters = val ? [...val.map(f => ({ ...f }))] : [];
        }
      },
      deep: true,
    },
  },

  methods: {
    /** Public: the parent section header's "Add filter" button calls this via ref. */
    addFilter() {
      this.filters.push({ column: null, operator: 'equals', value: '' });
    },

    removeFilter(index) {
      this.filters.splice(index, 1);
      this.emitChange();
    },

    onColumnChange(filter) {
      const colType = this.getColumnType(filter.column);
      const operators = this.getOperatorsForType(colType);
      if (!operators.find(op => op.value === filter.operator)) {
        filter.operator = operators[0]?.value || 'equals';
      }
      filter.value = filterValueForOperator(filter.operator, '');
      this.emitChange();
    },

    /** Converts the value to the new operator's shape: [from, to] for between, one value otherwise. */
    onOperatorChange(filter) {
      filter.value = filterValueForOperator(filter.operator, filter.value);
      this.emitChange();
    },

    isBetweenOperator(operator) {
      return operator === BETWEEN_OPERATOR;
    },

    /** [from, to] of a between filter; reads legacy 'a,b' values without rewriting them. */
    betweenEnds(filter) {
      return parseBetweenValue(filter.value);
    },

    betweenPlaceholder(endIndex) {
      return BETWEEN_PLACEHOLDERS[endIndex];
    },

    /** Replaces one end with a new [from, to] array, never mutating the current value. */
    setBetweenEnd(filter, endIndex, value) {
      filter.value = withBetweenPart(filter.value, endIndex, value);
      this.emitChange();
    },

    isDatePickerType(dataType) {
      return dataType === DATE_PICKER_SQL_TYPE;
    },

    emitChange() {
      this.$emit('input', this.filters.filter(f => f.column));
    },

    getColumnType(columnName) {
      const col = this.columns.find(c => c.name === columnName);
      return col ? col.dataType : 'nvarchar';
    },

    isNumericType(dataType) {
      return NUMERIC_SQL_TYPES.includes(dataType);
    },

    friendlyTypeLabel(dataType) {
      return friendlyType(dataType).label;
    },

    isNullOperator(operator) {
      return operator === 'is_null' || operator === 'is_not_null';
    },

    typeIcon(dataType) {
      if (this.isNumericType(dataType)) return 'mdi-numeric';
      if (DATE_SQL_TYPES.includes(dataType)) return 'mdi-calendar';
      if (dataType === 'bit') return 'mdi-toggle-switch';
      if (dataType === 'uniqueidentifier') return 'mdi-identifier';
      return 'mdi-format-text';
    },

    getOperatorsForType(dataType) {
      const base = [
        { label: 'Is blank', value: 'is_null' },
        { label: 'Is not blank', value: 'is_not_null' },
      ];

      if (this.isNumericType(dataType)) {
        return [
          { label: 'Is', value: 'equals' },
          { label: 'Is not', value: 'not_equals' },
          { label: 'Greater Than', value: 'gt' },
          { label: 'Less Than', value: 'lt' },
          { label: 'Greater or Equal', value: 'gte' },
          { label: 'Less or Equal', value: 'lte' },
          { label: 'Between', value: 'between' },
          ...base,
        ];
      }

      if (DATE_SQL_TYPES.includes(dataType)) {
        return [
          { label: 'Is', value: 'equals' },
          { label: 'After', value: 'gt' },
          { label: 'Before', value: 'lt' },
          { label: 'Between', value: 'between' },
          ...base,
        ];
      }

      // String types
      return [
        { label: 'Is', value: 'equals' },
        { label: 'Is not', value: 'not_equals' },
        { label: 'Contains', value: 'contains' },
        { label: 'Is one of', value: 'in' },
        ...base,
      ];
    },

    operatorsForColumn(columnName) {
      return this.getOperatorsForType(this.getColumnType(columnName));
    },
  },
};
</script>
